import { UserRole } from "./UserRole";

export enum EInputType {
  MANUALLY_ENTERED = "Manually Entered",
  SCAN = "Scan",
}

export enum ELineItemStatus {
  PENDING = "Pending",
  APPROVED = "Approved",
  REJECTED = "Rejected",
}

export enum EReviewState {
  PENDING_VENDOR_REVIEW = "Pending Vendor Review",
  SUBMITTED_TO_IMS = "Submitted to IMS",
  REJECTED_PENDING_ADMIN_REVIEW = "Rejected · Pending Admin Review",
  ACCEPTED = "Accepted",
  REJECTED_NEW_INVOICE_GENERATED = "Rejected · New Invoice Generated",
  REJECTED_NEEDS_MANUAL_INTERVENTION = "Rejected · Needs Manual Intervention",
}

export enum EPaymentStatus {
  PENDING_APPROVAL = "Pending Approval",
  PENDING_PAYMENT = "Pending Payment",
  REJECTED = "Rejected",
  PAID = "Paid",
}

export enum EInvoiceStatusCode {
  NEW = "NEW",
  APPROVED = "APPROVED",
  SUBMITTED = "SUBMITTED",
  PAID = "PAID",
  REJECTED = "REJECTED",
  SUPERSEDED = "SUPERSEDED",
}

export const INVOICE_STATUS_LABELS: Record<
  EInvoiceStatusCode,
  { vendor: string; superAdmin: string }
> = {
  [EInvoiceStatusCode.NEW]: {
    vendor: "Pending Approval",
    superAdmin: "Pending Vendor Approval",
  },
  [EInvoiceStatusCode.APPROVED]: {
    vendor: "Approved",
    superAdmin: "Approved by Vendor",
  },
  [EInvoiceStatusCode.SUBMITTED]: {
    vendor: "Submitted to IMS",
    superAdmin: "Submitted to IMS",
  },
  [EInvoiceStatusCode.PAID]: { vendor: "Paid", superAdmin: "Paid" },
  [EInvoiceStatusCode.REJECTED]: {
    vendor: "Pending Admin Approval",
    superAdmin: "Pending Approval",
  },
  [EInvoiceStatusCode.SUPERSEDED]: { vendor: "", superAdmin: "" },
};

export const INVOICE_STATUS_TILE_ORDER: EInvoiceStatusCode[] = [
  EInvoiceStatusCode.NEW,
  EInvoiceStatusCode.APPROVED,
  EInvoiceStatusCode.SUBMITTED,
  EInvoiceStatusCode.REJECTED,
  EInvoiceStatusCode.PAID,
];

export const normalizeInvoiceStatusCode = (raw: unknown): string =>
  String(raw ?? "")
    .trim()
    .toUpperCase();

const STATUS_CODE_ALIASES: Record<string, EInvoiceStatusCode> = {
  [EInvoiceStatusCode.NEW]: EInvoiceStatusCode.NEW,
  "PENDING APPROVAL": EInvoiceStatusCode.NEW,
  "PENDING VENDOR REVIEW": EInvoiceStatusCode.NEW,
  [EInvoiceStatusCode.APPROVED]: EInvoiceStatusCode.APPROVED,
  ACCEPTED: EInvoiceStatusCode.APPROVED,
  [EInvoiceStatusCode.SUBMITTED]: EInvoiceStatusCode.SUBMITTED,
  "SUBMITTED TO IMS": EInvoiceStatusCode.SUBMITTED,
  [EInvoiceStatusCode.PAID]: EInvoiceStatusCode.PAID,
  [EInvoiceStatusCode.REJECTED]: EInvoiceStatusCode.REJECTED,
  [EInvoiceStatusCode.SUPERSEDED]: EInvoiceStatusCode.SUPERSEDED,
};

export const resolveInvoiceStatusCode = (raw: unknown): EInvoiceStatusCode | "" =>
  STATUS_CODE_ALIASES[normalizeInvoiceStatusCode(raw)] ?? "";

export const isSuperAdminRole = (role: UserRole | string | null | undefined) =>
  role === UserRole.SUPER_ADMIN || role === UserRole.CARNVAL_ADMIN;

const warnedStatusCodes = new Set<string>();

export const getInvoiceStatusLabel = (
  code: string,
  role: UserRole | null
): string => {
  const labels = INVOICE_STATUS_LABELS[code as EInvoiceStatusCode];
  if (!labels) {
    if (!warnedStatusCodes.has(code)) {
      warnedStatusCodes.add(code);
      console.warn("[invoicing] unknown invoice_status", code);
    }
    return code;
  }
  return isSuperAdminRole(role) ? labels.superAdmin : labels.vendor;
};

export const isSupersededInvoice = (code: string): boolean =>
  normalizeInvoiceStatusCode(code) === EInvoiceStatusCode.SUPERSEDED;

export const REASONS_REQUIRING_SCANS = new Set<string>(["MISSING_SCAN"]);

export const reasonRequiresScans = (rejectionReasonCd?: string): boolean =>
  !!rejectionReasonCd && REASONS_REQUIRING_SCANS.has(rejectionReasonCd);

export const INTERNAL_REJECTION_REASON_TYPE = "INTERNAL";
export const INTERNAL_REJECTION_VENDOR_LABEL = "Pending Admin Approval";

export const isInternalRejectionReason = (
  rejectionReasonCd: string | undefined,
  internalReasonCodes: string[] | undefined
): boolean =>
  !!rejectionReasonCd &&
  (!internalReasonCodes || internalReasonCodes.includes(rejectionReasonCd));

export const VENDOR_INVOICE_ID_MAX_LENGTH = 30;
export const VENDOR_INVOICE_ID_PATTERN = /^[A-Za-z0-9]+$/;

export const TM_ID_MAX_LENGTH = 6;

export const BILL_TO = {
  name: "Carnival Cruise Line",
  addressLines: ["3655 NW 87th Avenue", "Miami, FL 33178", "United States"],
} as const;

export const VENDOR_INVOICE_ID_RULE = `Letters and numbers only, up to ${VENDOR_INVOICE_ID_MAX_LENGTH} characters.`;

export const validateVendorInvoiceId = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed === "") return "";
  if (trimmed.length > VENDOR_INVOICE_ID_MAX_LENGTH) {
    return `Too long — ${VENDOR_INVOICE_ID_MAX_LENGTH} characters maximum.`;
  }
  if (!VENDOR_INVOICE_ID_PATTERN.test(trimmed)) {
    return "Letters and numbers only — no spaces, dashes or other punctuation.";
  }
  return "";
};

export const APPROVE_REMARKS_MAX_LENGTH = 1000;
export const REJECT_COMMENTS_MAX_LENGTH = 1000;
